import React, { useState, useEffect, useCallback } from 'react'
import { 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar,
  ListItemButton,
  Avatar, 
  Box, 
  Typography, 
  Divider, 
  IconButton, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Paper,
  Badge,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert
} from '@mui/material'
// eslint-disable-next-line
import { formatDistanceToNow, format } from 'date-fns'
import { sv } from 'date-fns/locale'
import AddIcon from '@mui/icons-material/Add'
import PhoneIcon from '@mui/icons-material/Phone'
import ReceiptIcon from '@mui/icons-material/Receipt'
import PersonIcon from '@mui/icons-material/Person'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import { useNavigate } from 'react-router-dom'

// Helper functions to handle conversation states
function translateState(state) {
  switch (state) {
    case 'awaiting_initial_feedback':
      return 'Väntar på feedback'
    case 'awaiting_review_response':
      return 'Väntar på recensionssvar'
    case 'manual_conversation':
      return 'Manuell konversation'
    case 'completed':
      return 'Avslutad'
    default:
      return state
  }
}

function getStateColor(state) {
  switch (state) {
    case 'awaiting_initial_feedback':
      return { bgColor: '#E8F3FF', textColor: '#2563EB' } // Info blue
    case 'awaiting_review_response':
      return { bgColor: '#FFF7E6', textColor: '#D97706' } // Warning amber
    case 'manual_conversation':
      return { bgColor: '#ECFDF5', textColor: '#059669' } // Success green
    case 'completed':
      return { bgColor: '#EFF6FF', textColor: '#3730A3' } // Primary indigo
    default:
      return { bgColor: '#F3F4F6', textColor: '#4B5563' } // Grey
  }
}

// Add a new function to check user settings
const fetchSettings = async () => {
  try {
    const response = await api.get('/settings');
    const settings = response.data;
    const name = settings.name || '';
    const companyName = settings.company?.name || '';
    const reviewLink = settings.company?.reviewLink || '';
    
    // Check if any required settings are missing
    return {
      complete: !!(name && companyName && reviewLink),
      settings: {
        name,
        companyName,
        reviewLink
      }
    };
  } catch (err) {
    console.error('Error checking user settings:', err);
    return { complete: false, settings: {} };
  }
}

const ConversationsList = ({ onSelectConversation, selectedConversationId }) => {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [phoneError, setPhoneError] = useState(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  const { user } = useAuth()
  const theme = useTheme()
  // eslint-disable-next-line
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // eslint-disable-next-line
  const navigate = useNavigate()
  
  // Add state for settings check
  const [showSettingsAlert, setShowSettingsAlert] = useState(false)
  const [sendToSelfLoading, setSendToSelfLoading] = useState(false)
  const [dialogMode, setDialogMode] = useState('regular') // 'regular' or 'getStarted'

  const fetchConversations = useCallback(async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const response = await api.get('/users/conversations')
      console.log('Conversation data:', response.data)  // Log the response data
      setConversations(response.data)
    } catch (err) {
      console.error('Error fetching conversations:', err)
      setError('Kunde inte ladda konversationer')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  // Debug log the first conversation structure when it changes
  useEffect(() => {
    if (conversations && conversations.length > 0) {
      console.log('First conversation structure:', conversations[0]);
      // Log if customer_name exists
      console.log('Has customer_name:', 'customer_name' in conversations[0]);
      console.log('Customer name value:', conversations[0].customer_name);
    }
  }, [conversations]);

  const handleOpenDialog = async () => {
    // Check if user has completed settings first
    const { complete } = await fetchSettings();
    
    if (complete) {
      // Settings complete, proceed with creating new conversation
      setDialogMode('regular');
      setOpenDialog(true);
      setPhoneNumber('');
      setPhoneError(null);
    } else {
      // Settings incomplete, show alert
      setShowSettingsAlert(true);
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPhoneNumber('');
    setCustomerName('');
    setPhoneError('');
    setDialogMode('regular'); // Reset to regular mode
  }

  const handleCloseSettingsAlert = () => {
    setShowSettingsAlert(false)
  }

  const goToMessageTemplates = () => {
    // Navigate to message templates page using tab change mechanism
    // This dispatches a custom event that the App component can listen for
    const event = new CustomEvent('app:changeTab', { 
      detail: { 
        tab: 'templates' 
      } 
    });
    window.dispatchEvent(event);
    
    // Close the alert dialog
    handleCloseSettingsAlert();
  }

  const cleanPhoneNumber = (number) => {
    // Remove spaces, dashes, parentheses and other non-digit characters except +
    return number.replace(/[^\d+]/g, '')
  }

  const validatePhoneNumber = (number) => {
    // Clean the number first
    const cleanedNumber = cleanPhoneNumber(number)
    
    // Check if it's already in E.164 format (starting with +46 for Sweden)
    const e164Regex = /^\+46[1-9]\d{7,9}$/
    if (e164Regex.test(cleanedNumber)) {
      return true
    }
    
    // Check if it's a Swedish mobile number without country code
    // Swedish mobile numbers typically start with 07X
    const swedishMobileRegex = /^0(7[02369])\d{7,8}$/
    return swedishMobileRegex.test(cleanedNumber)
  }
  
  const formatToE164 = (number) => {
    // Clean the number first
    const cleanedNumber = cleanPhoneNumber(number)
    
    // If already in E.164 format, return as is
    if (cleanedNumber.startsWith('+')) {
      return cleanedNumber
    }
    
    // If it's a Swedish mobile number starting with 0, convert to +46
    if (cleanedNumber.startsWith('0')) {
      return '+46' + cleanedNumber.substring(1)
    }
    
    // If no conversion rule applies, add +46 prefix
    return '+46' + cleanedNumber
  }

  const handleCreateConversation = async () => {
    try {
      // Clean the phone number
      const cleanedNumber = cleanPhoneNumber(phoneNumber);
      
      if (!cleanedNumber) {
        setPhoneError('Mobilnummer får inte vara tomt');
        return;
      }
      
      if (!validatePhoneNumber(cleanedNumber)) {
        setPhoneError('Ange ett giltigt svenskt mobilnummer (t.ex. 070-123 45 67 eller +46701234567)');
        return;
      }

      setDialogLoading(true);
      
      // Format to E.164
      const formattedNumber = formatToE164(cleanedNumber);
      
      console.log(`Creating conversation with number: ${formattedNumber}`);
      console.log(`Customer name: ${customerName || 'Not provided'}`);
      
      // Make API call to create conversation
      const response = await api.post('/users/create-conversation', {
        number: formattedNumber,
        customer_name: customerName || undefined // Only send if it has a value
      });
      
      console.log('Conversation created:', response.data);
      
      // Close dialog and refresh conversations
      setOpenDialog(false);
      setPhoneNumber('');
      setCustomerName('');
      setPhoneError('');
      
      // Refresh conversations
      fetchConversations();
      
      // Select the new conversation
      if (response.data && response.data.id) {
        onSelectConversation(response.data.id);
      }
      
    } catch (error) {
      console.error('Error creating conversation:', error);
      // Show appropriate error message
      setPhoneError('Ett fel uppstod. Försök igen.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSendToSelf = async () => {
    try {
      // Check if user has completed settings first
      const { complete, settings } = await fetchSettings();
      
      if (!complete) {
        setShowSettingsAlert(true);
        return;
      }

      if (!user.phone) {
        setError('Kunde inte hitta ditt telefonnummer');
        return;
      }

      setSendToSelfLoading(true);
      
      console.log(`Creating conversation for self with number: ${user.phone}`);
      console.log(`User name from settings: ${settings.name}`);
      
      // Make API call to create conversation for yourself
      const response = await api.post('/users/create-conversation', {
        number: user.phone,
        customer_name: settings.name || undefined
      });
      
      console.log('Self conversation created:', response.data);
      
      // Refresh conversations
      fetchConversations();
      
      // Select the new conversation
      if (response.data && response.data.id) {
        onSelectConversation(response.data.id);
      }
      
    } catch (error) {
      console.error('Error creating self conversation:', error);
      setError('Ett fel uppstod. Försök igen.');
    } finally {
      setSendToSelfLoading(false);
    }
  };

  const handleSendToSomeoneElse = async () => {
    // Check if user has completed settings first
    const { complete } = await fetchSettings();
    
    if (complete) {
      // Settings complete, proceed with creating new conversation
      setDialogMode('getStarted');
      setOpenDialog(true);
      setPhoneNumber('');
      setPhoneError(null);
    } else {
      // Settings incomplete, show alert
      setShowSettingsAlert(true);
    }
  };

  const handleHowItWorks = () => {
    window.open('https://www.relco.se/#sa-funkar-det', '_blank');
  };

  // Add a function to format time more compactly
  const formatCompactTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      
      if (diffMinutes < 1) {
        return 'nu';
      } else if (diffMinutes < 60) {
        return `${diffMinutes}m`;
      } else if (diffHours < 24) {
        return `${diffHours}h`;
      } else if (diffDays < 7) {
        return `${diffDays}d`;
      } else if (diffWeeks < 5) {
        return `${diffWeeks}v`;
      } else {
        // For older dates, show day and month (e.g., "5 maj")
        return format(date, 'd MMM', { locale: sv });
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  if (loading && conversations.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          flexDirection: 'column',
          p: 3
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Laddar konversationer...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          p: 3, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          flexDirection: 'column',
          height: '100%' 
        }}
      >
        <Typography variant="body1" color="error" gutterBottom>
          {error}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={fetchConversations}
          startIcon={<PhoneIcon />}
        >
          Försök igen
        </Button>
      </Box>
    )
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: { xs: 0, sm: 2 },
        borderRight: { xs: 0, md: `1px solid ${theme.palette.divider}` },
        overflow: 'hidden'
      }}
    >
      <Box 
        sx={{ 
          p: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Typography 
          variant="h6" 
          component="h2" 
          fontWeight="600"
          sx={{ 
            background: 'linear-gradient(45deg, #4F46E5 30%, #818CF8 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Ny recensionsförfrågan
        </Typography>
        {!isMobile && (
          <Tooltip title="Ny recensionsförfrågan">
            <IconButton 
              color="primary" 
              onClick={handleOpenDialog}
              aria-label="lägg till recensionsförfrågan"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      {!isMobile && conversations.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            Konversationer
          </Typography>
        </Box>
      )}
      
      {isMobile && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: `${theme.palette.primary.main}05`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.main}10`,
            },
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}
          onClick={handleOpenDialog}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              mr: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <AddIcon sx={{ color: '#fff' }} />
          </Box>
          <Typography variant="body1" fontWeight={500}>
            Ny recensionsförfrågan
          </Typography>
        </Box>
      )}
      
      {conversations.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            p: 3,
            mx: 2,
            my: 3,
            borderRadius: 2, // Smaller border radius
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            textAlign: 'left', // Left align text
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
          }}
        >
          {/* Header */}
          <Typography 
            variant="h5" 
            component="h2" 
            fontWeight="600"
            sx={{ mb: 1 }}
          >
            Kom igång gratis
          </Typography>
          
          {/* Subtitle */}
          <Typography 
            variant="body1" 
            sx={{ mb: 3, opacity: 0.9 }}
          >
            Skicka din första recensionsförfrågan.
          </Typography>
          
          {/* Send to yourself button */}
          <Button 
            variant="outlined"
            onClick={handleSendToSelf}
            disabled={sendToSelfLoading}
            fullWidth
            sx={{ 
              mb: 1.5,
              bgcolor: 'white !important',
              color: theme.palette.text.primary,
              borderColor: 'white',
              fontWeight: 600,
              py: 1.2,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9) !important',
                borderColor: 'white',
              },
              '&:disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.7) !important',
                color: 'rgba(0, 0, 0, 0.5)',
                borderColor: 'rgba(255, 255, 255, 0.7)',
              }
            }}
          >
            {sendToSelfLoading ? <CircularProgress size={20} color="inherit" /> : 'Skicka till dig själv'}
          </Button>
          
          {/* Description for send to yourself */}
          <Typography 
            variant="body2" 
            sx={{ mb: 2, opacity: 0.8, fontSize: '0.875rem' }}
          >
            Du får se hur det ser ut från kundens perspektiv.
          </Typography>
          
          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
            <Typography 
              variant="body2" 
              sx={{ mx: 2, opacity: 0.7, fontWeight: 500 }}
            >
              eller
            </Typography>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
          </Box>
          
          {/* Send to someone else button */}
          <Button 
            variant="outlined"
            onClick={handleSendToSomeoneElse}
            fullWidth
            sx={{ 
              mb: 1.5,
              bgcolor: 'white !important',
              color: theme.palette.text.primary,
              borderColor: 'white',
              fontWeight: 600,
              py: 1.2,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9) !important',
                borderColor: 'white',
              }
            }}
          >
            Skicka till någon annan
          </Button>
          
          {/* Description for send to someone else */}
          <Typography 
            variant="body2" 
            sx={{ mb: 3, opacity: 0.8, fontSize: '0.875rem' }}
          >
            T.ex. till en kollega.
          </Typography>
          
          {/* How it works button */}
          <Button 
            variant="outlined"
            onClick={handleHowItWorks}
            fullWidth
            sx={{ 
              color: 'white',
              borderColor: 'white',
              fontWeight: 500,
              py: 1.2,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'white',
              }
            }}
          >
            Så funkar det
          </Button>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List disablePadding>
            {conversations.map((conversation, index) => (
              <React.Fragment key={conversation.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedConversationId === conversation.id}
                    onClick={() => onSelectConversation(conversation.id)}
                    sx={{ 
                      px: 2,
                      py: 1.5,
                      transition: 'all 0.2s',
                      '&.Mui-selected': {
                        backgroundColor: `${theme.palette.primary.main}15`,
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        '&:hover': {
                          backgroundColor: `${theme.palette.primary.main}25`,
                        }
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="success"
                        variant="dot"
                        invisible={!conversation.unread_count}
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: getStateColor(conversation.state).bgColor,
                            color: getStateColor(conversation.state).textColor
                          }}
                        />
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body1" 
                          component="span" 
                          fontWeight={conversation.unread_count ? 600 : 400}
                          noWrap
                        >
                          {conversation.customer_name || conversation.phone_number}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            noWrap
                            sx={{ 
                              opacity: 0.8,
                              fontWeight: conversation.unread_count ? 500 : 400,
                              flexGrow: 1
                            }}
                          >
                            <span 
                              style={{ 
                                color: getStateColor(conversation.state).textColor,
                                backgroundColor: getStateColor(conversation.state).bgColor,
                                padding: '1px 5px',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                display: 'inline-block'
                              }}
                            >
                              {translateState(conversation.state)}
                            </span>
                            {conversation.last_message && (
                              <span style={{ marginLeft: '6px', color: theme.palette.text.secondary, opacity: 0.8 }}>
                                {conversation.last_message.slice(0, 20)}{conversation.last_message.length > 20 ? '...' : ''}
                              </span>
                            )}
                          </Typography>
                        </Box>
                      }
                      sx={{ mr: 1 }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    {conversation.updated_at && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: '0.7rem',
                          minWidth: '30px',
                          textAlign: 'right'
                        }}
                      >
                        {formatCompactTime(conversation.updated_at)}
                      </Typography>
                    )}
                  </ListItemButton>
                </ListItem>
                {index < conversations.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
      
      {/* Settings Alert Dialog */}
      <Dialog
        open={showSettingsAlert}
        onClose={handleCloseSettingsAlert}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Inställningar krävs</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              För att skapa en recensionsförfrågan behöver du först komplettera dina inställningar.
            </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            Du behöver ange följande obligatoriska uppgifter:
          </Typography>
          <Box sx={{ pl: 2, mb: 3 }}>
            <Typography variant="body2" component="li" gutterBottom>
              Ditt namn
            </Typography>
            <Typography variant="body2" component="li" gutterBottom>
              Företagsnamn
            </Typography>
            <Typography variant="body2" component="li" gutterBottom>
              Recensionslänk
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Dessa uppgifter används i meddelandemallarna som skickas till dina kunder.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettingsAlert}>Avbryt</Button>
          <Button onClick={goToMessageTemplates} variant="contained" color="primary">
            Gå till inställningar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Phone Number Input Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Ny recensionsförfrågan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ange kontaktuppgifter
          </Typography>
          <TextField
            autoFocus
            label="Mobilnummer"
            placeholder="070-123 45 67"
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            error={!!phoneError}
            helperText={phoneError}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <PhoneIcon color="action" fontSize="small" sx={{ mr: 1 }} />
              ),
            }}
          />
          <TextField
            label="Kundens namn (valfritt)"
            placeholder="Namn Efternamn"
            fullWidth
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <PersonIcon color="action" fontSize="small" sx={{ mr: 1 }} />
              ),
            }}
          />
          
          {/* Fortnox alternative button - only show in regular mode */}
          {dialogMode === 'regular' && (
            <Box sx={{ mt: 3, mb: 1 }}>
              <Divider sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  eller
                </Typography>
              </Divider>
              <Button 
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => {
                  handleCloseDialog();
                  // Navigate to Fortnox tab
                  const event = new CustomEvent('app:changeTab', { 
                    detail: { tab: 'fortnox' } 
                  });
                  window.dispatchEvent(event);
                }}
                startIcon={<ReceiptIcon />}
              >
                Använd Fortnox-integration
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Skicka recensionsförfrågningar till kunder du har fakturerat
              </Typography>
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit">Avbryt</Button>
          <Button 
            onClick={handleCreateConversation} 
            variant="contained"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Skapa förfrågan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default ConversationsList 